import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { RESET_ALL as RESET_ON_LOGOUT, danger, success } from 'redux-store/reducers/common-reducer';

import { API, get, post, put } from 'utils/api';
import { Querystring } from 'utils/url';
import { snakeCaseKeys } from 'utils/keys';
import { logError } from 'utils/log';
import { createReducer } from 'utils/redux';
import { __t, applyTranslations } from 'utils/language';

import type { Action, Pagination, ThunkAction } from 'types';
import type {
  RealmProfile,
  RealmProfileEMSResourceMatches,
  RealmProfileForm,
  RealmProfileTag
} from '../types/RealmProfile.type';
import type { ImportRealmProfilesResponse } from 'modules/realm-outfits/types/AdminRealmProfiles.type';
import type { Role } from 'types/State.type';

applyTranslations(require('./admin-realm-profiles.i18n.json'));
applyTranslations(require('../components/realm-outfit-review/realm-outfit-review.i18n.json'));

const PAGINATION_LIMIT = 50;

const PROFILES_LOADED = 'admin_realm_profiles/PROFILES_LOADED';
const PROFILE_UPDATED = 'admin_realm_profiles/PROFILE_UPDATED';
const PROFILES_RESET = 'admin_realm_profiles/PROFILES_RESET';
const APPROVING_PROFILE = 'admin_realm_profiles/APPROVING_PROFILE';
const SET_INITIAL_SHOW_OUTFITS = 'admin_realm_profiles/SET_INITIAL_SHOW_OUTFITS';
const UPDATE_SHOW_OUTFITS = 'admin_realm_profiles/UPDATE_SHOW_OUTFITS';

const PROFILE_TAGS_LOADED = 'admin_realm_profiles/PROFILE_TAGS_LOADED';
const PROFILE_TAGS_RESET = 'admin_realm_profiles/PROFILE_TAGS_RESET';

type State = {
  tags: Array<RealmProfileTag>,
  profiles: Array<RealmProfile>,
  pagination: Pagination,
  isStatusUpdating: boolean,
  showOutfits: {
    scp: boolean,
    ccm: boolean
  }
};

const initialState: State = {
  tags: [],
  profiles: [],
  pagination: {},
  isStatusUpdating: false,
  showOutfits: {
    scp: false,
    ccm: false
  }
};

type ProfileParamsType = {
  page: number,
  itemsPerPage: number,
  approvalStatus?: string,
  outfitTypes?: string
}

function profilesLoaded(data): Action {
  return {
    type: PROFILES_LOADED,
    payload: {
      data
    }
  };
}

function profileTagsLoaded(data): Action {
  return {
    type: PROFILE_TAGS_LOADED,
    payload: {
      data
    }
  };
}

export function resetProfileTags(): Action {
  return {
    type: PROFILE_TAGS_RESET
  };
}

export function resetProfiles(): Action {
  return {
    type: PROFILES_RESET
  };
}

function profileUpdated(profileId: number, updatedProfile: RealmProfileForm) {
  return {
    type: PROFILE_UPDATED,
    payload: {
      profileId,
      updatedProfile
    }
  };
}

function setApprovingProfileStatus(isStatusUpdating: boolean): Action {
  return {
    type: APPROVING_PROFILE,
    payload: {
      isStatusUpdating
    }
  };
}

export const setInitiaShowOutfits = (roles: Array<Role>): Action => ({
  type: SET_INITIAL_SHOW_OUTFITS,
  payload: {
    roles
  }
});

export const updateShowOutfits = (value: boolean, part: keyof State['showOutfits']): Action => ({
  type: UPDATE_SHOW_OUTFITS,
  payload: {
    value,
    part
  }
});

export function fetchProfiles(query) {
  const approvalStatus = query.status === 'all' ? '' : (query.status || '');
  const params: ProfileParamsType = {
    page: query.page || 1,
    itemsPerPage: PAGINATION_LIMIT
  };

  if (approvalStatus) {
    params.approvalStatus = approvalStatus;
  }

  return async (dispatch, getState) => {
    const { showOutfits } = getState().adminRealmProfiles;
    const include = Object
      .keys(showOutfits)
      .map(key => (showOutfits[key] ? key : null))
      .filter(v => v !== null)
      .join(',');

    params.outfitTypes = include;

    const queryString = Querystring.objectToString(snakeCaseKeys(params));
    const url = `${API.END_POINTS.ADMIN.REALM.getProfiles()}?${queryString}`;

    dispatch(showLoading(url));

    try {
      const response = await get(url);
      dispatch(profilesLoaded(response.data));
    } catch (error) {
      dispatch(hideLoading(url));
      logError(error);
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export function updateRealmProfile(profile: RealmProfileForm) {
  return async (dispatch) => {
    try {
      const profileId = profile.id;
      const url = API.END_POINTS.ADMIN.REALM.saveProfile(profile.id);

      const response = await put(url, {
        data: profile
      });

      dispatch(profileUpdated(profileId, response.data));
    } catch (error) {
      const errorStatus = error.response?.data?.status;
      let errorMsg = __t('realm_outfit_review.message_failed');

      if (errorStatus === 403) {
        errorMsg = __t('realm_outfit_review.errors.not_authorized');
      }

      if (errorStatus === 424) {
        errorMsg = __t('realm_outfit_review.errors.dependent_service');
      }

      if (errorStatus === 500) {
        errorMsg = __t('realm_outfit_review.errors.server_error');
      }

      logError(error);

      dispatch(danger(errorMsg));
    }
  };
}

export function updateEMSProfileStatus(
  profileId: number,
  status: 'approved' | 'declined',
  declineReason?: string): ThunkAction {
  const url = API.END_POINTS.ADMIN.REALM.updateProfileStatus(profileId);

  return async (dispatch) => {
    try {
      if (status === 'approved') {
        dispatch(setApprovingProfileStatus(true));
      }

      await put(url, {
        data: {
          status,
          declineReason
        }
      },
      { suppressGlobalNotification: true }
      );

      dispatch(fetchProfiles({}));
      dispatch(setApprovingProfileStatus(false));
      dispatch(success(__t('admin_realm_profiles.message_success')));
    } catch (error) {
      logError(error);
      dispatch(setApprovingProfileStatus(false));
      dispatch(danger(__t('admin_realm_profiles.message_failed')));
    }
  };
}

export function fetchProfileTags(): ThunkAction {
  return (dispatch) => {
    const url = API.END_POINTS.ADMIN.REALM.getProfilesTags();

    return get(url)
      .then((response) => {
        dispatch(profileTagsLoaded(response?.data || []));
      })
      .catch((error) => {
        logError(error);
      });
  };
}

const MIN_AVATAR_WIDTH = 600;
const MIN_AVATAR_HEIGHT = 600;

export const getAvatarValidationError = (imageProps) => {
  if (imageProps.area.width * imageProps.area.originalWidth < MIN_AVATAR_WIDTH
    || imageProps.area.height * imageProps.area.originalHeight < MIN_AVATAR_HEIGHT) {
    return 'dimension';
  }

  if (Math.round(imageProps.area.width * imageProps.area.originalWidth)
    !== Math.round(imageProps.area.height * imageProps.area.originalHeight)) {
    return 'dimension_ratio';
  }

  return null;
};

export const getCoverImageValidationError = (imageProps, dimension) => {
  if (Math.round(imageProps.area.width * imageProps.area.originalWidth * imageProps.area.scale) < dimension.width
    || Math.round(imageProps.area.height * imageProps.area.originalHeight * imageProps.area.scale) < dimension.height) {
    return 'cover_dimension';
  }

  return null;
};

export function uploadImage(influencerProfile, type, image) {
  const url = API.END_POINTS.API.IMAGES;

  return (dispatch) => {
    if (!image.file) {
      // just area was updated
      return Promise.resolve(image);
    }

    dispatch(showLoading(url));

    const data = new FormData();

    data.append('file', image.file);

    return post(url, { data })
      .then((response) => {
        const createdImage = {
          area: image.area,
          ...response.data
        };

        dispatch(hideLoading(url));

        return createdImage;
      })
      .catch((error) => {
        dispatch(hideLoading(url));
        logError(error);

        throw error;
      });
  };
}

export function importRealmProfiles(profileIds: Array<number>) {
  return async (): Promise<ImportRealmProfilesResponse | undefined> => {
    const url = API.END_POINTS.ADMIN.REALM.importProfiles();

    try {
      const { data } = await post(url, {
        data: { influencerIds: profileIds }
      });

      return data;
    } catch (error) {
      logError(error);
    }

    return undefined;
  };
}

/**
 *
 * When called witout emsId then it returns the existing match. or nothing.
 */
export function matchRealmProfileWithEMSProfile(influencerId: number, emsId?: string) {
  return async (): Promise<null | RealmProfile> => {
    const url = API.END_POINTS.ADMIN.REALM.matchOutfitCreators(influencerId, emsId);

    try {
      const response = await put(url);

      if (response.status === 204) {
        return null;
      }

      return response.data as RealmProfile;
    } catch (error) {
      logError(error);
    }

    return null;
  };
}

export function getMatchesOfRealmProfileWithEMSProfiles(influencerId: number) {
  return async (): Promise<RealmProfileEMSResourceMatches> => {
    const url = API.END_POINTS.ADMIN.REALM.matchOutfitCreators(influencerId);

    try {
      const response = await get(url);

      if (response.status === 204) {
        return { matches: [] };
      }

      return response.data as RealmProfileEMSResourceMatches;
    } catch (error) {
      logError(error);
    }

    return { matches: [] };
  };
}

export default createReducer<State, any>('adminRealmProfiles', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [PROFILES_RESET]: (state) => ({
    ...state,
    profiles: [],
    pagination: {}
  }),
  [PROFILE_TAGS_RESET]: (state) => ({
    ...state,
    tags: []
  }),
  [PROFILE_UPDATED]: (state, { profileId, updatedProfile }) => ({
    ...state,
    profiles: (state.profiles || []).map(profile => {
      if (profile.id === profileId) {
        return updatedProfile;
      }

      return profile;
    })
  }),
  [PROFILE_TAGS_LOADED]: (state, { data }) => ({
    ...state,
    tags: data
  }),
  [PROFILES_LOADED]: (state, { data }) => ({
    ...state,
    profiles: data.data,
    pagination: {
      ...data.meta,
      links: data.links
    }
  }),
  [APPROVING_PROFILE]: (state, { isStatusUpdating }) => ({
    ...state,
    isStatusUpdating
  }),
  [SET_INITIAL_SHOW_OUTFITS]: (state, { roles }) => ({
    ...state,
    showOutfits: {
      scp: roles.includes('ADMIN') || roles.includes('INFLUENCER_REALM'),
      ccm: roles.includes('ADMIN') || roles.includes('CCM_MODERATOR')
    }
  }),
  [UPDATE_SHOW_OUTFITS]: (state, { value, part }) => ({
    ...state,
    showOutfits: {
      ...state.showOutfits,
      [part]: value
    }
  })
});
